import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, fmt, fmtDelta } from "../../lib/game";
import { Button } from "../ui/button";
import { Sprout, Trophy, Loader2, Sparkles, Lightbulb, CheckCircle2 } from "lucide-react";

export const ShareView = ({ sessionId, onPlay }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/sessions/${sessionId}`).then(({ data }) => setData(data)).catch(() => setData(false));
  }, [sessionId]);

  if (data === null)
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 text-emerald-400 animate-spin" /></div>;
  if (data === false)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="text-slate-400">This shared result could not be found.</p>
        <Button onClick={onPlay} className="bg-gradient-to-r from-emerald-500 to-teal-600 text-slate-950 font-bold">Play Burgeon</Button>
      </div>
    );

  const { session, review } = data;
  const change = session.net_worth - (session.starting_net_worth ?? 0);

  return (
    <div className="bg-grid min-h-screen px-4 py-10">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2.5 mb-6">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
            <Sprout className="w-5 h-5 text-slate-950" strokeWidth={2.4} />
          </div>
          <span className="font-heading text-xl font-bold text-slate-100">Burgeon</span>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl border border-slate-800 bg-slate-900/70 p-6 sm:p-8 text-center"
        >
          <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mb-4">
            <Trophy className="w-7 h-7 text-slate-950" />
          </div>
          <p className="text-xs font-semibold uppercase tracking-widest text-emerald-400">Simulation Result</p>
          <h1 className="font-heading text-3xl font-bold text-slate-50 mt-2">{session.nickname}'s journey</h1>
          {review?.persona && (
            <span className="inline-block mt-3 text-sm font-semibold px-3 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-300">
              {review.persona}
            </span>
          )}
          <div className="mt-5">
            <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Final Net Worth</p>
            <p className="font-mono text-4xl font-bold text-slate-50 mt-1">{fmt(session.net_worth)}</p>
            <p className={`text-sm font-mono font-semibold mt-1 ${change >= 0 ? "text-emerald-400" : "text-red-400"}`}>
              {fmtDelta(change)} across 8 rounds
            </p>
          </div>
        </motion.div>

        {review && (
          <div className="mt-5 rounded-3xl border border-emerald-500/30 bg-slate-900/70 p-6">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-emerald-400" />
              <span className="text-xs font-semibold uppercase tracking-widest text-emerald-400">AI Coach Review</span>
            </div>
            <p className="text-slate-300 leading-relaxed text-[15px]">{review.summary}</p>
            {review.strengths?.length > 0 && (
              <ul className="mt-4 space-y-2">
                {review.strengths.map((s, i) => (
                  <li key={i} className="text-sm text-slate-300 flex gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /><span>{s}</span>
                  </li>
                ))}
              </ul>
            )}
            {review.reflections?.length > 0 && (
              <div className="mt-4 rounded-2xl border border-blue-500/25 bg-blue-500/[0.06] p-3.5">
                <p className="text-xs font-semibold uppercase tracking-wider text-blue-300 mb-1.5">What you said vs what you did</p>
                <ul className="space-y-1.5">
                  {review.reflections.map((r, i) => (
                    <li key={i} className="text-sm text-slate-200 flex gap-2"><span className="text-blue-400 mt-0.5">•</span><span>{r}</span></li>
                  ))}
                </ul>
              </div>
            )}
            {review.tip && (
              <div className="mt-4 rounded-2xl border border-yellow-500/30 bg-yellow-500/[0.07] p-3.5 flex gap-2.5">
                <Lightbulb className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
                <p className="text-sm text-slate-200">{review.tip}</p>
              </div>
            )}
          </div>
        )}

        <div className="text-center mt-6">
          <Button
            data-testid="share-play-btn"
            onClick={onPlay}
            className="h-12 px-8 bg-gradient-to-r from-emerald-500 to-teal-600 text-slate-950 font-bold rounded-xl"
          >
            Play your own game
          </Button>
        </div>
      </div>
    </div>
  );
};
